/**
 * Utility functions for generating workspace names and managing saved workspace states
 */

/**
 * Generate initials from workspace name
 * Example: "Executive Assistant [0.5]" -> "EA"
 */
export function generateWorkspaceInitials(workspaceName: string): string {
  // Remove version numbers in brackets and normalize separators
  const cleanName = workspaceName
    .replace(/\[[\d.]+\]/g, '') // Remove version numbers like [0.5]
    .replace(/[^\w\s]/g, ' ') // Replace special characters with spaces
    .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
    .trim();
    
  // Split by spaces and take first letter of each word
  const words = cleanName.split(/\s+/).filter(word => word.length > 0);
  const initials = words.map(word => word.charAt(0).toUpperCase()).join('');
  
  return initials;
}

/**
 * Parse workspace initials and AI name from existing saved workspace name
 * Example: "EA Andromeda 3" -> { initials: "EA", aiName: "Andromeda", instanceNumber: 3 }
 */
export function parseWorkspaceName(savedName: string): { initials: string; aiName: string; instanceNumber?: number } | null {
  // Match pattern: "<initials> <aiName> <optional number>"
  const match = savedName.match(/^([A-Z]+)\s+(\w+)(?:\s+(\d+))?$/);
  
  if (!match) {
    return null;
  }
  
  return {
    initials: match[1],
    aiName: match[2],
    instanceNumber: match[3] ? parseInt(match[3], 10) : undefined
  };
}

/**
 * Find the next instance number for a given workspace type (initials + aiName)
 * Returns undefined if this would be the first instance
 */
export function getNextInstanceNumber(existingNames: string[], initials: string, aiName: string): number | undefined {
  const pattern = new RegExp(`^${initials}\\s+${aiName}(?:\\s+(\\d+))?$`);
  
  let maxInstance = 0;
  let hasExisting = false;
  
  for (const name of existingNames) {
    const match = name.match(pattern);
    if (match) {
      hasExisting = true;
      const instanceNum = match[1] ? parseInt(match[1], 10) : 1; // If no number, treat as instance 1
      maxInstance = Math.max(maxInstance, instanceNum);
    }
  }
  
  // If no existing instances, don't add a number
  if (!hasExisting) {
    return undefined;
  }
  
  // Return next number in sequence
  return maxInstance + 1;
}

/**
 * Generate a new workspace name using the format: "<initials> <aiName> <instanceNumber?>"
 */
export function generateWorkspaceName(workspaceName: string, aiName: string, existingNames: string[]): string {
  const initials = generateWorkspaceInitials(workspaceName);
  const instanceNumber = getNextInstanceNumber(existingNames, initials, aiName);
  
  if (instanceNumber === undefined) {
    return `${initials} ${aiName}`;
  } else {
    return `${initials} ${aiName} ${instanceNumber}`;
  }
}